/**
 * Broadcast tarp:critical — regra de domínio #3 (GUPM-169).
 * ----------------------------------------------------------
 * Toda alteração em `tarp_records` (via datasource) emite no canal
 * `tarp:critical`. A UI assina via `subscribeTarpCritical` para invalidar
 * queries em tempo real.
 *
 * O choke point é o datasource (regra #2: a UI nunca escreve direto), então
 * emitir aqui cobre todas as mutações do app. Hardening futuro: trigger no DB
 * (`realtime.send`) para cobrir escritas fora do app.
 */

import { supabase } from '@/lib/supabase';
import type { TarpRecordStatus } from '@/domain/tarp/status';

export const TARP_CRITICAL_CHANNEL = 'tarp:critical';

// 'open'/'update'/'close' são eventos de NEGÓCIO (no futuro também notificam
// humano); 'refresh' só sincroniza a tela (invalidar query). O rascunho usa
// 'refresh' — propaga a mudança pra todos SEM alertar ninguém (GUPM-178).
export type TarpCriticalEvent = 'open' | 'update' | 'close' | 'refresh';

export interface TarpCriticalPayload {
  event: TarpCriticalEvent;
  id: string;
  damId: string | null;
  instrumentId: string | null;
  status: TarpRecordStatus;
}

/** Contrato do payload — puro e testável. */
export function buildTarpCriticalPayload(
  event: TarpCriticalEvent,
  record: { id: string; damId: string | null; instrumentId: string | null; status: TarpRecordStatus },
): TarpCriticalPayload {
  return {
    event,
    id: record.id,
    damId: record.damId,
    instrumentId: record.instrumentId,
    status: record.status,
  };
}

// ── Transporte (I/O) com seam de teste ────────────────────────
type Emitter = (payload: TarpCriticalPayload) => void;
type Listener = (payload: TarpCriticalPayload) => void;

/**
 * UM canal para emitir E receber (GUPM-234).
 *
 * Antes havia dois: o emissor publicava em `tarp:critical` e o assinante escutava
 * em `tarp:critical:sub`. Broadcast do Supabase é roteado por TÓPICO, então nada
 * era entregue — a regra de domínio #3 estava inoperante e só o polling de 30s
 * segurava a tela. Tópicos diferentes não isolam o eco do próprio emissor; quem
 * faz isso é `broadcast.self: false`, que já está configurado aqui.
 *
 * Os assinantes vivem num Set sobre este canal único: `unsubscribe` remove o
 * listener e deixa o canal de pé (ele é do app inteiro, não de uma tela).
 */
const listeners = new Set<Listener>();
let sharedChannel: ReturnType<typeof supabase.channel> | null = null;

function getSharedChannel() {
  if (!sharedChannel) {
    const channel = supabase.channel(TARP_CRITICAL_CHANNEL, {
      config: { broadcast: { self: false } },
    });
    channel.on('broadcast', { event: 'change' }, ({ payload }) => {
      for (const listener of listeners) listener(payload as TarpCriticalPayload);
    });
    channel.subscribe();
    sharedChannel = channel;
  }
  return sharedChannel;
}

function defaultEmit(payload: TarpCriticalPayload): void {
  // Best-effort: o registro já foi persistido; o broadcast é sinal de refresh
  // para outros clientes. Não bloqueia nem lança em caso de falha de rede.
  void getSharedChannel().send({ type: 'broadcast', event: 'change', payload });
}

let emitter: Emitter = defaultEmit;

export function emitTarpCritical(payload: TarpCriticalPayload): void {
  emitter(payload);
}

/** Test seam — injeta um emissor espião; `null` restaura o default. */
export function __setTarpCriticalEmitter(fn: Emitter | null): void {
  emitter = fn ?? defaultEmit;
}

/** Assina o canal; chama `onChange` a cada broadcast. Retorna unsubscribe. */
export function subscribeTarpCritical(
  onChange: (payload: TarpCriticalPayload) => void,
): () => void {
  getSharedChannel();
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}
